import React, { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { buildLoginPageInnerHtml, loginStageVideo } from '../constants/loginPageWidget';

/**
 * O vídeo do palco fica fora do HTML que o editor recria.
 * No visualizar e no Moodle o player entra no HTML publicado e não pisca.
 */
const StageVideo = memo(function StageVideo({ kind, src, poster }) {
  if (kind === 'iframe') {
    return (
      <div className="madri-login__streamwrap">
        <iframe className="madri-login__stream" src={src} title="" tabIndex={-1} allow="autoplay; encrypted-media" />
      </div>
    );
  }
  if (kind === 'video') {
    return <video className="madri-login__bg" src={src} poster={poster || undefined} muted autoPlay loop playsInline />;
  }
  return null;
});

/**
 * Palco da página de login. A largura segue a folha do curso (boxed ou total),
 * sem esticar até a área cinza ao redor do palco.
 */
export default function RenderSiteLogin({ content = {}, widgetId }) {
  const slotRef = useRef(null);
  const [bleed, setBleed] = useState(null);
  const [videoSlot, setVideoSlot] = useState(null);
  const contentKey = JSON.stringify(content || {});
  const html = useMemo(
    () => buildLoginPageInnerHtml(content, widgetId, { stage: true }),
    [contentKey, widgetId]
  );
  const video = loginStageVideo(content.videoUrl);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const host = slot?.parentElement;
    const canvas = slot?.closest('.course-wrapper');
    if (!host || !canvas) return undefined;
    const measure = () => {
      const hostBox = host.getBoundingClientRect();
      const canvasBox = canvas.getBoundingClientRect();
      const width = Math.round(canvasBox.width);
      const marginLeft = Math.round(canvasBox.left - hostBox.left);
      setBleed((prev) => {
        if (prev && Math.abs(prev.width - width) < 2 && Math.abs(prev.marginLeft - marginLeft) < 2) return prev;
        return { width, marginLeft };
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useLayoutEffect(() => {
    const node = slotRef.current?.querySelector('[data-gp-login-stage-video]');
    setVideoSlot(node || null);
  }, [html]);

  return (
    <>
      <div
        ref={slotRef}
        style={bleed ? { width: bleed.width, marginLeft: bleed.marginLeft, maxWidth: 'none' } : undefined}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {videoSlot && video.src
        ? createPortal(
          <StageVideo kind={video.kind} src={video.src} poster={content.imageUrl || ''} />,
          videoSlot
        )
        : null}
    </>
  );
}
